"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import  { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

import { Button } from "ui/core/button";
import { Input } from "ui/core/input";
import { Select } from "ui/core/select";

import { useSendContactEmail } from "../services/send-contact-email";

const getCategoryList = ({ lang }: { lang: Lang }) =>
  lang === "zh"
    ? ([
        "註冊 / 登入",
        "忘記密碼",
        "智能理財相關",
        "個股探勘相關",
        "企業合作",
        "其他",
      ] as const)
    : ([
        "Register / Login",
        "Forget Password",
        "Robo-advisor",
        "Stock Mining",
        "Business Corporation",
        "Others",
      ] as const);

export const ContactForm = ({ className }: { className?: string }) => {
  const { t, lang } = useLang();
  const categories = getCategoryList({ lang });
  const { mutate: sendEmail } = useSendContactEmail();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactFormSchema = z.object({
    username: z.string().min(1),
    email: z.string().min(1).email(),
    category: z.enum(categories),
    content: z.string().min(1),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<z.infer<typeof contactFormSchema>>({
    mode: "onTouched",
    defaultValues: {
      username: "",
      email: "",
      content: "",
      category: undefined,
    },
    resolver: zodResolver(contactFormSchema),
  });

  const onSubmit: SubmitHandler<z.infer<typeof contactFormSchema>> = ({
    username,
    email,
    category,
    content,
  }) => {
    setIsSubmitting(true);

    sendEmail(
      {
        username,
        email,
        content: `【${category}】: ${content}`,
      },
      {
        onSuccess: () => {
          toast.success(t.contactUs.success);
          setIsSubmitting(false);
          reset();
        },
        onError: () => {
          toast.error(t.contactUs.fail);
        },
      }
    );
  };

  return (
    <form className={twMerge("flex flex-col gap-4", className)}>
      <Input
        {...register("username")}
        placeholder={t.contactUs.username}
        className="focus:ring-[#20995D]"
        aria-label="enter username"
      />
      <Input
        {...register("email")}
        type="email"
        placeholder={t.contactUs.email}
        className="focus:ring-[#20995D]"
        aria-label="enter email address"
      />
      <Select
        required
        {...register("category")}
        placeholder={t.contactUs.category}
        options={categories.map((category) => category)}
        className="focus:ring-[#20995D]"
        aria-label="select question category"
      />
      <div className="overflow-hidden rounded-lg shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-[#20995D]">
        <textarea
          {...register("content")}
          defaultValue=""
          placeholder={t.contactUs.content}
          rows={8}
          className="block w-full resize-none border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
          aria-label="enter message content"
        />
      </div>
      <Button
        type="submit"
        className="bg-[#2ABA77] hover:bg-[#20995D] disabled:hover:bg-[#20995D]"
        aria-label="submit"
        disabled={!isValid}
        isLoading={isSubmitting}
        onClick={handleSubmit(onSubmit)}
      >
        {t.contactUs.submit}
      </Button>
    </form>
  );
};
